import React, { useCallback, useId, useRef, useState } from 'react';
import './OrbBall.css';

/**
 * Each orb mood gets its own palette. Moods come from the backend
 * (see backend/lib/orbEngine.js MOODS); `default` covers the idle orb before
 * a mood has been revealed. `core`/`mid`/`edge`/`glow` are also used by the
 * share card (utils/prophecyCard.js).
 */
export const MOOD_PALETTES = {
  // beige
  default: { core: '#fffaf0', mid: '#f3d59a', deep: '#c4914a', edge: '#5a3a12', swirl: '#fff2d2', glow: 'rgba(255, 206, 130, 0.7)', gem: '#f5f3ff', gemDeep: '#8f97b5', petal: '#fff1dc', petalDeep: '#e2b77c' },
  // yellow / fire
  Wise: { core: '#fffbe6', mid: '#ffb21e', deep: '#d94f00', edge: '#3d0d00', swirl: '#fff0b3', glow: 'rgba(255, 150, 20, 0.72)', gem: '#ffb21e', gemDeep: '#a34700', petal: '#ffcf5a', petalDeep: '#e56a00' },
  Sarcastic: { core: '#eafff8', mid: '#19d3a6', deep: '#006b58', edge: '#001f19', swirl: '#b8ffec', glow: 'rgba(25, 211, 166, 0.65)', gem: '#19d3a6', gemDeep: '#005244', petal: '#7cf0d2', petalDeep: '#0a9e7c' },
  // blue
  Sleepy: { core: '#e8f7ff', mid: '#2d8cff', deep: '#0a2fb8', edge: '#010631', swirl: '#bfe8ff', glow: 'rgba(40, 130, 255, 0.72)', gem: '#3f8cff', gemDeep: '#0a2a8a', petal: '#6fc0ff', petalDeep: '#1552e0' },
  // red
  Overdramatic: { core: '#fff0f0', mid: '#ff2a4a', deep: '#a80020', edge: '#260006', swirl: '#ffc2cc', glow: 'rgba(255, 40, 70, 0.72)', gem: '#ff3355', gemDeep: '#8a0018', petal: '#ff7a90', petalDeep: '#d10a33' },
  // purple
  Philosophical: { core: '#fbeaff', mid: '#c04dff', deep: '#5a0fb0', edge: '#12022f', swirl: '#f0c8ff', glow: 'rgba(190, 70, 255, 0.72)', gem: '#c04dff', gemDeep: '#4a0a90', petal: '#e39bff', petalDeep: '#9b2de0' },
  Chaotic: { core: '#fff0fa', mid: '#ff3fae', deep: '#a0006a', edge: '#2a0019', swirl: '#ffd0ee', glow: 'rgba(255, 60, 170, 0.7)', gem: '#ff3fae', gemDeep: '#80004f', petal: '#ff8fd0', petalDeep: '#d4118a' },
};

// Starfield inside the glass, in the swirl SVG's 0–100 space.
const STARS = [
  [28, 30, 0.9], [40, 22, 0.6], [62, 26, 1.1], [72, 38, 0.7], [34, 48, 0.8],
  [55, 42, 1.3], [78, 55, 0.6], [24, 62, 0.7], [46, 64, 1], [66, 70, 0.8],
  [38, 78, 0.6], [58, 82, 0.7], [50, 30, 0.5], [30, 40, 0.5], [70, 62, 1.1],
  [44, 52, 0.5], [84, 44, 0.5], [18, 50, 0.6], [60, 14, 0.6], [36, 88, 0.5],
  [52, 72, 0.9], [26, 22, 0.5], [76, 24, 0.7], [64, 50, 0.6], [14, 36, 0.4],
];

// Petals drifting around the orb: position as % of the stage, base rotation, scale.
const PETALS = [
  { left: 5, top: 20, rot: -30, scale: 0.9, delay: 0 },
  { left: 0, top: 38, rot: -58, scale: 1.1, delay: 0.8 },
  { left: 3, top: 57, rot: -82, scale: 1, delay: 1.6 },
  { left: 13, top: 72, rot: -112, scale: 0.7, delay: 2.4 },
  { left: 87, top: 18, rot: 30, scale: 0.85, delay: 0.4 },
  { left: 92, top: 36, rot: 58, scale: 1.1, delay: 1.2 },
  { left: 89, top: 55, rot: 82, scale: 1, delay: 2 },
  { left: 80, top: 70, rot: 112, scale: 0.7, delay: 2.8 },
];

const SPARKLES = [
  { left: 16, top: 4, scale: 1, delay: 0 },
  { left: 81, top: 2, scale: 0.85, delay: 1.3 },
  { left: 9, top: 30, scale: 0.55, delay: 0.7 },
  { left: 90, top: 27, scale: 0.6, delay: 2 },
];

// Light ribbons inside the glass (0–100 space). w = stroke width, o = opacity.
const SWIRL_GROUPS = [
  [
    { d: 'M14 72 C30 38 66 64 86 26', w: 3, o: 1 },
    { d: 'M20 30 C40 16 62 46 80 76', w: 1.6, o: 0.85 },
    { d: 'M8 50 C26 30 50 34 60 50 C70 66 86 60 94 46', w: 1.1, o: 0.7 },
  ],
  [
    { d: 'M50 96 C30 74 66 58 46 34 C38 22 54 12 64 18', w: 2.6, o: 1 },
    { d: 'M26 58 C40 50 58 70 76 50', w: 1.2, o: 0.7 },
  ],
];

const SPARKLE_PATH = 'M10 0 C11 7 13 9 20 10 C13 11 11 13 10 20 C9 13 7 11 0 10 C7 9 9 7 10 0 Z';

/**
 * The orb has no face. Like a cartoon object that emotes through effects, it
 * shows how it feels with light and motion, picked by the screen from what the
 * player is doing (see the `.expr-*` rules in OrbBall.css):
 *  idle     – calm, slow breathing glow
 *  curious  – tilts and leans in, a ring of light orbits it
 *  thinking – motes of light circle while the glow throbs
 *  excited  – rapid pulses, crackling sparkles, energy rings
 *  happy    – warm golden radiance, slow rotating rays, sparkles floating up
 */
const EXPRESSIONS = ['idle', 'curious', 'thinking', 'excited', 'happy'];

// Crystal facets in the 0–100 space: [points, shade]. Light ones catch the glint.
const FACETS = [
  ['50,4 72,20 50,34 28,20', 0.34],
  ['72,20 94,42 74,50 50,34', 0.16],
  ['28,20 50,34 26,50 6,42', 0.26],
  ['50,34 74,50 50,66 26,50', 0.1],
  ['74,50 94,42 88,72 66,80', 0.2],
  ['26,50 6,42 12,72 34,80', 0.3],
  ['74,50 66,80 50,66', 0.08],
  ['26,50 50,66 34,80', 0.14],
  ['50,66 66,80 50,96 34,80', 0.24],
];

// Motes that orbit (curious/thinking) or rise (happy): angle offset + delay.
const MOTES = [0, 1, 2, 3, 4, 5].map((i) => ({ a: i * 60, delay: -i * 0.45, x: 12 + ((i * 17) % 76) }));

/** Effects that live outside the glass so they can spill past its edge. */
function OrbFx() {
  return (
    <div className="orb-fx" aria-hidden="true">
      <div className="orb-rays" />
      <div className="orb-ring orb-ring-a" />
      <div className="orb-ring orb-ring-b" />
      <div className="orb-orbit">
        {MOTES.map((m, i) => (
          <span key={i} className="orb-mote" style={{ '--a': `${m.a}deg`, animationDelay: `${m.delay}s` }} />
        ))}
      </div>
      <div className="orb-rise">
        {MOTES.map((m, i) => (
          <svg key={i} className="orb-rise-spark" viewBox="0 0 20 20" style={{ left: `${m.x}%`, animationDelay: `${m.delay * 2}s` }}>
            <path d={SPARKLE_PATH} fill="#fff" />
          </svg>
        ))}
      </div>
    </div>
  );
}

/**
 * @param {number} intensity 0–4, drives shake/glow/spin escalation via a CSS var
 * @param {function} onTap called on every tap when tappable
 */
export default function OrbBall({
  mood = 'default',
  size = 200,
  intensity = 0,
  tappable = false,
  shaking = false,
  revealing = false,
  thinking = false,
  showBase = true,
  expression = 'idle',
  onTap,
  ariaLabel = 'The orb',
}) {
  const palette = MOOD_PALETTES[mood] || MOOD_PALETTES.default;
  const uid = useId().replace(/:/g, '');
  const [ripples, setRipples] = useState([]);
  const [bouncing, setBouncing] = useState(false);
  const rippleId = useRef(0);

  const handleTap = useCallback(() => {
    if (!tappable) return;

    const id = rippleId.current++;
    setRipples((prev) => [...prev, id]);
    setTimeout(() => setRipples((prev) => prev.filter((r) => r !== id)), 650);

    setBouncing(true);
    setTimeout(() => setBouncing(false), 280);

    onTap?.();
  }, [tappable, onTap]);

  const wrapClasses = [
    'orb-wrap',
    tappable ? 'is-tappable' : '',
    shaking ? 'is-shaking' : '',
    bouncing ? 'is-bouncing' : '',
    revealing ? 'is-revealing' : '',
    thinking ? 'is-thinking' : '',
    `expr-${EXPRESSIONS.includes(expression) ? expression : 'idle'}`,
  ].filter(Boolean).join(' ');

  const id = (name) => `${uid}-${name}`;

  return (
    <div
      className={`orb-stage${showBase ? '' : ' no-base'}`}
      style={{
        '--orb-size': `${size}px`,
        '--orb-core': palette.core,
        '--orb-mid': palette.mid,
        '--orb-deep': palette.deep,
        '--orb-edge': palette.edge,
        '--orb-swirl': palette.swirl,
        '--orb-glow': palette.glow,
        '--orb-intensity': intensity,
      }}
    >
      {showBase && (
        <div className="orb-petals" aria-hidden="true">
          {PETALS.map((p, i) => (
            <svg
              key={i}
              className="orb-petal"
              viewBox="0 0 20 40"
              style={{ left: `${p.left}%`, top: `${p.top}%`, '--r': `${p.rot}deg`, '--s': p.scale, animationDelay: `${p.delay}s` }}
            >
              <path d="M10 0 C19 10 19 29 10 40 C1 29 1 10 10 0 Z" fill={`url(#${id('petal')})`} />
              <path d="M10 5 C11 16 11 26 10 35" stroke="rgba(255,255,255,0.55)" strokeWidth="0.8" fill="none" />
            </svg>
          ))}
          {SPARKLES.map((s, i) => (
            <svg
              key={i}
              className="orb-sparkle"
              viewBox="0 0 20 20"
              style={{ left: `${s.left}%`, top: `${s.top}%`, '--s': s.scale, animationDelay: `${s.delay}s` }}
            >
              <path d={SPARKLE_PATH} fill={palette.swirl} />
            </svg>
          ))}
        </div>
      )}

      <div
        className={wrapClasses}
        onPointerDown={handleTap}
        role={tappable ? 'button' : 'img'}
        aria-label={ariaLabel}
        tabIndex={tappable ? 0 : -1}
        onKeyDown={(e) => {
          if (tappable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleTap();
          }
        }}
      >
        <div className="orb-halo" />
        <OrbFx />
        <div className="orb-ball">
          <div className="orb-nebula" />
          <div className="orb-nebula orb-nebula-b" />
          <div className="orb-vignette" />
          <div className="orb-core" />
          <svg className="orb-swirls" viewBox="0 0 100 100" aria-hidden="true">
            <defs>
              <linearGradient id={id('ribbon')} x1="0" y1="1" x2="1" y2="0">
                <stop offset="0" stopColor={palette.swirl} stopOpacity="0" />
                <stop offset="0.5" stopColor="#ffffff" stopOpacity="1" />
                <stop offset="1" stopColor={palette.swirl} stopOpacity="0" />
              </linearGradient>
              <filter id={id('soft')} x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2.4" />
              </filter>
              <filter id={id('glow')} x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="1.4" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            {SWIRL_GROUPS.map((group, gi) => (
              <g key={gi} className={`orb-swirl-${gi ? 'b' : 'a'}`}>
                {group.map(({ d, w, o }, pi) => (
                  <React.Fragment key={pi}>
                    <path d={d} stroke={palette.mid} strokeWidth={w * 3.4} fill="none" opacity={0.55 * o} filter={`url(#${id('soft')})`} />
                    <path d={d} stroke={`url(#${id('ribbon')})`} strokeWidth={w} fill="none" strokeLinecap="round" opacity={0.7 * o} />
                    <path
                      className="orb-flow"
                      d={d}
                      pathLength="100"
                      stroke="#fff"
                      strokeWidth={w * 0.9}
                      fill="none"
                      strokeLinecap="round"
                      filter={`url(#${id('glow')})`}
                      style={{ animationDelay: `${-(gi * 1.1 + pi * 0.7)}s` }}
                    />
                  </React.Fragment>
                ))}
              </g>
            ))}
            <g className="orb-stars">
              {STARS.map(([cx, cy, r], i) => (
                <circle key={i} cx={cx} cy={cy} r={r} fill="#fff" style={{ animationDelay: `${(i % 6) * 0.55}s` }} />
              ))}
            </g>
          </svg>
          <svg className="orb-facets" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {FACETS.map(([points, shade], i) => (
              <polygon
                key={i}
                points={points}
                className="orb-facet"
                style={{ '--shade': shade, animationDelay: `${-i * 0.7}s` }}
              />
            ))}
          </svg>
          <div className="orb-sheen" />
          <div className="orb-rim" />
          <div className="orb-glint" />
        </div>
        {ripples.map((rid) => <span key={rid} className="orb-ripple" />)}
      </div>

      {showBase && (
        <svg className="orb-pedestal" viewBox="0 0 200 100" aria-hidden="true">
          <defs>
            <linearGradient id={id('gold')} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#7a4a10" />
              <stop offset="0.18" stopColor="#d99a34" />
              <stop offset="0.38" stopColor="#fff0b8" />
              <stop offset="0.55" stopColor="#e7b04a" />
              <stop offset="0.8" stopColor="#a8661a" />
              <stop offset="1" stopColor="#6a3d0c" />
            </linearGradient>
            <linearGradient id={id('goldTop')} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#fff3c4" />
              <stop offset="1" stopColor="#d6982e" />
            </linearGradient>
            <linearGradient id={id('gem')} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#ffffff" />
              <stop offset="0.45" stopColor={palette.gem} />
              <stop offset="1" stopColor={palette.gemDeep} />
            </linearGradient>
            <linearGradient id={id('petal')} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={palette.petal} />
              <stop offset="1" stopColor={palette.petalDeep} />
            </linearGradient>
            <pattern id={id('filigree')} width="16" height="14" patternUnits="userSpaceOnUse">
              <path d="M0 7 C3 1 7 1 8 7 C9 13 13 13 16 7" stroke="#5e3508" strokeWidth="1.1" fill="none" opacity="0.75" />
              <circle cx="4" cy="3.5" r="1" fill="#5e3508" opacity="0.6" />
              <circle cx="12" cy="10.5" r="1" fill="#5e3508" opacity="0.6" />
            </pattern>
            <clipPath id={id('band')}>
              <path d="M10 52 A90 13 0 0 0 190 52 L190 72 A90 13 0 0 1 10 72 Z" />
            </clipPath>
          </defs>

          <ellipse cx="100" cy="90" rx="98" ry="8" fill="rgba(59, 20, 37, 0.22)" />
          {/* Base: bottom rim, filigree band, top face */}
          <ellipse cx="100" cy="74" rx="93" ry="14" fill="#6a3d0c" />
          <ellipse cx="100" cy="73" rx="92" ry="13" fill={`url(#${id('gold')})`} opacity="0.55" />
          <path d="M10 52 A90 13 0 0 0 190 52 L190 72 A90 13 0 0 1 10 72 Z" fill={`url(#${id('gold')})`} />
          <rect x="10" y="48" width="180" height="40" fill={`url(#${id('filigree')})`} clipPath={`url(#${id('band')})`} />
          <path d="M10 56 A90 13 0 0 0 190 56" stroke="#5e3508" strokeWidth="0.8" fill="none" opacity="0.5" />
          <path d="M10 70 A90 13 0 0 0 190 70" stroke="#fff0b8" strokeWidth="1" fill="none" opacity="0.6" />
          <ellipse cx="100" cy="52" rx="90" ry="13" fill={`url(#${id('goldTop')})`} />
          <ellipse cx="100" cy="52" rx="74" ry="9.5" fill="none" stroke="#a8661a" strokeWidth="1.2" opacity="0.75" />

          {/* Neck with a flared collar, and the cup that cradles the ball */}
          <path d="M74 26 L126 26 L120 40 L136 48 Q100 58 64 48 L80 40 Z" fill={`url(#${id('gold')})`} />
          <path d="M66 46 Q100 54 134 46" stroke="#fff3c4" strokeWidth="1" fill="none" opacity="0.7" />
          <path d="M50 6 Q54 28 80 31 L120 31 Q146 28 150 6 Q100 22 50 6 Z" fill={`url(#${id('gold')})`} />
          <path d="M50 6 Q100 22 150 6" stroke="#fff3c4" strokeWidth="1.8" fill="none" />
          <path d="M56 15 Q100 30 144 15" stroke="#5e3508" strokeWidth="0.9" fill="none" opacity="0.55" />
          <path d="M60 19 C68 24 74 22 80 26 M140 19 C132 24 126 22 120 26" stroke="#5e3508" strokeWidth="0.8" fill="none" opacity="0.5" />

          {/* Gem set into the band */}
          <path d="M100 46 L118 66 L100 88 L82 66 Z" fill={`url(#${id('gold')})`} />
          <path className="orb-gem" d="M100 50 L114 66 L100 84 L86 66 Z" fill={`url(#${id('gem')})`} />
          <path d="M86 66 L114 66 M100 50 L94 66 L100 84 M100 50 L106 66 L100 84" stroke="rgba(255,255,255,0.75)" strokeWidth="0.7" fill="none" />
        </svg>
      )}
      {showBase && <div className="orb-spill" aria-hidden="true" />}
    </div>
  );
}
